import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser } from '_admin/features/users/usersSelectors';
import Avatar from "react-avatar"
import DynamicTable from '@components/Table/index';
import UserMiniProfile from '@components/UserMiniProfile';
import Toggle from '@components/Toggle/index';
import Pagination from '../../../Pagination';
import DialogueBox from '../../../DialogueBox';
import ConfirmationPrompt from '../../../ConfirmationPrompt';
import Icon from '../../../../icons';
import { useUserList } from '../hooks/useUserList';
import { useUserActions } from '../hooks/useUserActions';
import { showToast } from '@container/SpockViewPage/actions/action';
import { useModules } from '@utils/modules';

const TABS = ['All', 'Active', 'Temporary', 'Blocked'];
const USER_STATUS_BADGE_CONFIG = {
	BLOCKED: { className: 'status__blocked status__blocked__filled', text: 'Blocked' },
	TEMPORARY: { className: 'status__temp status__temp__filled', text: 'Temp' },
	ACTIVE: { className: 'status__accepted status__accepted__filled', text: 'Active' },
};

const getUserStatusBadges = (row, activeFilter) => {
	if (activeFilter !== 'All') return [];
	const badges = [];
	if (row.isBlocked) badges.push(USER_STATUS_BADGE_CONFIG.BLOCKED);
	if (row.isTempUser) badges.push(USER_STATUS_BADGE_CONFIG.TEMPORARY);
	if (row.isActive) badges.push(USER_STATUS_BADGE_CONFIG.ACTIVE);
	return badges;
};

const CONFIRMATION_CONFIG = {
	block: {
		title: 'BLOCK USER',
		message: 'Are you sure you want to block this user?',
		confirmText: 'Block',
		confirmButtonClass: 'btn btnRed',
	},
	unblock: {
		title: 'UNBLOCK USER',
		message: 'Do you want to unblock this user?',
	},
	resetPassword: {
		title: 'RESET PASSWORD',
		message: 'Are you sure you want to reset password for this user?',
	},
};

const UsersList = ({ loggedInUserId, onAddUser, onEditUser, onViewProfile, onManageWorkload }) => {
	const reduxDispatch = useDispatch();
	const currentUser = useSelector(selectCurrentUser);
	const { isModuleEnabled } = useModules();

	const {
		users,
		totalCount,
		loading,
		listError,
		successMessage,
		searchTerm,
		activeFilter,
		handleSearchChange,
		handleClearSearch,
		handleFilterChange,
		// Sorting is not required for now — see note near the sort UI below.
		// sortOrder,
		// handleSortToggle,
		currentPage,
		totalPages,
		itemsPerPage,
		goToPage,
		refreshUsers,
	} = useUserList({ loggedInUserId, itemsPerPage: 100 });

	const {
		confirmation,
		showBlockConfirm,
		showUnblockConfirm,
		showResetPasswordConfirm,
		dismissConfirmation,
		executeAction,
		successMessage: actionSuccessMessage,
		dismissSuccessMessage,
		blockError,
		unblockError,
		resetPasswordError,
	} = useUserActions();

	useEffect(() => {
		if (actionSuccessMessage) {
			reduxDispatch(showToast({
				message: actionSuccessMessage,
				type: 'success',
				iconType: 'success',
				autoClose: true,
				delayTime: 3000,
				showActions: false,
			}));
			dismissSuccessMessage();
			refreshUsers();
		}
	}, [actionSuccessMessage, reduxDispatch, dismissSuccessMessage, refreshUsers]);

	useEffect(() => {
		const errMsg = blockError || unblockError || resetPasswordError;
		if (errMsg) {
			reduxDispatch(showToast({
				message: errMsg,
				type: 'warning',
				iconType: 'warning',
				autoClose: true,
				delayTime: 5000,
				showActions: false,
			}));
		}
	}, [blockError, unblockError, resetPasswordError, reduxDispatch]);

	const [openDropdownId, setOpenDropdownId] = useState(null);
	const [pendingToggleUser, setPendingToggleUser] = useState(null);

	// Close dropdown on outside click
	useEffect(() => {
		if (!openDropdownId) return;
		const handleClickOutside = (e) => {
			if (!e.target.closest('.dropDown')) {
				setOpenDropdownId(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [openDropdownId]);

	const toggleDropdown = useCallback((id) => {
		setOpenDropdownId((prev) => (prev === id ? null : id));
	}, []);

	// ── Navigation handlers ──
	const handleEditUser = useCallback((user) => {
		onEditUser(user);
	}, [onEditUser]);

	const handleViewProfile = useCallback((user) => {
		onViewProfile(user);
	}, [onViewProfile]);

	const handleAddUser = useCallback(() => {
		onAddUser();
	}, [onAddUser]);

	const handleManageWorkload = useCallback((user, type) => {
		onManageWorkload(user, type);
	}, [onManageWorkload]);

	const onSearchInputChange = useCallback((e) => {
		handleSearchChange(e.target.value);
	}, [handleSearchChange]);

	// ── Toggle access (block/unblock with confirmation) ──
	const handleToggleAccess = useCallback((user) => {
		setPendingToggleUser(user);
		if (user.isActive) {
			showBlockConfirm(user);
		} else {
			showUnblockConfirm(user);
		}
	}, [showBlockConfirm, showUnblockConfirm]);

	const handleConfirmAction = useCallback(() => {
		executeAction();
		setPendingToggleUser(null);
	}, [executeAction]);

	const handleCancelAction = useCallback(() => {
		dismissConfirmation();
		setPendingToggleUser(null);
	}, [dismissConfirmation]);

	const pagingModel = useMemo(() => ({
		TotalCount: totalCount,
		Rows: itemsPerPage,
		Page: currentPage + 1,
	}), [totalCount, itemsPerPage, currentPage]);

	const handlePagination = useCallback((page) => {
		goToPage(page - 1);
	}, [goToPage]);

	const confirmationMeta = confirmation ? CONFIRMATION_CONFIG[confirmation.type] : null;

	// ── Column definitions ──
	const columns = useMemo(() => [
		{
			key: 'name',
			header: 'Name',
			render: (row) => (
				<div className="userGroup">
					<div className="rowFlex">
						<div className="userPic userPicIcon" onClick={() => handleViewProfile(row)}>
							<Avatar
								round={true}
								size={"36"}
								name={row.displayName}
								maxInitials={2}
								style={{ marginTop: '5px'}}
							/>
						</div>

						<div className="userInfo colFlex">
							<span>
								<a href="javascript:void(0)" onClick={() => handleViewProfile(row)}>
									{row.displayName}
								</a>
								{getUserStatusBadges(row, activeFilter).map((badge) => (
									<span key={badge.text} className={`ml status statusFilled workkardStatus ${badge.className}`}>
										{badge.text}
									</span>
								))}
							</span>
							<label>{row.email}</label>
						</div>
					</div>
				</div>
			),
		},
		{
			key: 'JobTitle',
			header: 'Job Title',
			render: (row) => {
				return row.title || ''
			},
		},
		...(currentUser?.permissions?.can_view_workload ? [
			{
				key: 'Workkard',
				header: 'Workkard',
				// render: (row) => `${row.openWorkkardCount} Open and ${row.closeWorkkardCount} closed`,
				render: (row) => {
					const openCount = row.openWorkkardCount || 0;
					const closeCount = row.closeWorkkardCount || 0;
					return (
						<>
							{isModuleEnabled('admin.user.workload_counts.clickable') ? (
								<a
									href="javascript:void(0)"
									onClick={() => handleManageWorkload(row, 'workkards')}
								>
									{openCount}
								</a>
							) : (
								<span>{openCount}</span>
							)}
							{' Open and '}
							<span>{closeCount}</span>
							{' closed'}
						</>
					);
				}
			},
			{
				key: 'Activity',
				header: 'Activity',
				// render: (row) => `${row.openActivitiesCount} Open and ${row.closeActivitiesCount} closed`,
				render: (row) => {
					const openCount = row.openActivitiesCount || 0;
					const closeCount = row.closeActivitiesCount || 0;
					return (
						<>
							{isModuleEnabled('admin.user.workload_counts.clickable') ? (
								<a
									href="javascript:void(0)"
									onClick={() => handleManageWorkload(row, 'activities')}
								>
									{openCount}
								</a>
							) : (
								<span>{openCount}</span>
							)}
							{' Open and '}
							<span>{closeCount}</span>
							{' closed'}
						</>
					);
				}
			},
		] : []),
		{
			key: 'Access',
			header: 'Access',
			render: (row) => {
				if (row.isSelf) return null;
				if (!isModuleEnabled('admin.user.block')) return null;
				const canBlock = currentUser?.permissions?.can_block_a_user;
				return (
					<Toggle
						checked={row.isActive}
						onChange={() => handleToggleAccess(row)}
						disabled={!canBlock}
					/>
				)
			}
		},
		{
			key: 'Action',
			header: '',
			render: (row) => {
				const isOpen = openDropdownId === row.id;
				const canEdit = currentUser?.permissions?.can_edit_user && isModuleEnabled('admin.user.edit');
				const canReset = currentUser?.permissions?.can_reset_password && isModuleEnabled('admin.user.reset_password');
				return (
					<div className={`dropDown animated noteAction superFaste ${isOpen ? 'active' : ''}`}>
						<span className="icon" onClick={() => toggleDropdown(row.id)}>
							<Icon name="more" className="icon more" />
						</span>
						<div data-test="actions" className={`dropDownMenu openRight ${isOpen ? 'onClick' : ''}`}>
							{!row.isEmailVerified && !row.isSelf && (
								<a data-test="action-resend" href='javascript:void(0)'>Resend Verification</a>
							)}
							{canEdit ? (
								<a data-test="action-edit" href='javascript:void(0)' onClick={() => handleEditUser(row)}>Edit User</a>
							) : (
								<a data-test="action-edit" href='javascript:void(0)' className="disable_anchr">Edit User</a>
							)}
							{!row.isSelf && (canReset ? (
								<a data-test="action-reset-password" href='javascript:void(0)' onClick={() => { setOpenDropdownId(null); showResetPasswordConfirm(row); }}>Reset Password</a>
							) : (
								<a data-test="action-reset-password" href='javascript:void(0)' className="disable_anchr">Reset Password</a>
							))}
						</div>
					</div>
				);
			},
		},
	], [openDropdownId, handleToggleAccess, handleEditUser, handleViewProfile, handleManageWorkload, toggleDropdown, showResetPasswordConfirm, currentUser, activeFilter]);

	return (
		<div className=''>
			<div className='colFlex admin__header'>
				<div className="rowFlex alignMiddle rowSpaceBetween">
				<h1>Manage Users ({totalCount})</h1>
				<div>
				<div className='formGroup mrNone alignMiddle wth-4'>
					{/* <button className='btn-cc__primary btn-cc__gray'>
						<Icon name="filter" className="icon mr" />
						Filter
					</button> */}

					<div className="org_access__add--user">
						<div className="formGroup mrNone">
							<div className="search rowFlex mr">
								<input
									type='search'
									placeholder="Search by Name, Job Title or Email"
									value={searchTerm}
									onChange={onSearchInputChange}
								/>
								<div className="search__icon">
									<ul className="rowFlex">
										<li>
											{searchTerm ? (
												<a href="javascript:void(0)" onClick={handleClearSearch}>
													<Icon name="close" className="icon iconSmall" />
												</a>
											) : (
												<a href="javascript:void(0)">
													<Icon name="search" className="icon searchIcon" />
												</a>
											)}
										</li>
									</ul>
								</div>
							</div>
							<input type='button' value='Add User' className='btn-cc__primary' onClick={handleAddUser} />
						</div>
					</div>
				</div>
				</div>
				</div>

							<div className='alignMiddle'>
				<div className='tabNav mt'>
					<div className='tabs' data-test="tabs">
						{TABS.filter((tab) => tab !== 'Temporary' || isModuleEnabled('admin.user.manage_users.temp_tab')).map((tab) => (
							<a
								key={tab}
								href='javascript:void(0)'
								className={activeFilter === tab ? 'active' : ''}
								data-test={`tabls-${tab.toLowerCase()}`}
								onClick={() => handleFilterChange(tab)}
							>
								{tab}
							</a>
						))}
					</div>
				</div>
				<div className="toast toast__initial info">Workload last refreshed on 3/10/2026 at 6:01 PM <a href="javascript:void(0)" onClick={refreshUsers} className="ml">Refresh</a></div>
			</div>
			</div>


			<div className="admin__container__data admin__container__data--users">
				{loading && (
					<div className='alignMiddle' style={{ padding: '24px', justifyContent: 'center', color: '#999' }}>Loading users...</div>
				)}

				{!loading && !listError && users.length === 0 && (
					<div className='alignMiddle' style={{ padding: '24px', justifyContent: 'center', color: '#999' }}>No users found.</div>
				)}

				{listError && (
					<div className='alignMiddle' style={{ padding: '24px', justifyContent: 'center', color: '#f44336' }}>{listError}</div>
				)}

				{!loading && users.length > 0 && (
					<DynamicTable columns={columns} data={users} />
				)}
			</div>

			{totalPages > 1 && (
				<Pagination
					pagingModel={pagingModel}
					handlePagination={handlePagination}
				/>
			)}

			<DialogueBox
				overlayClassName="modal"
				contentClassName="modalContent animated faster fadeInUpx2 small"
				shouldCloseOnOverlayClick={false}
				isOpen={!!confirmation}
				onRequestClose={handleCancelAction}
			>
				<ConfirmationPrompt
					message={confirmationMeta?.message || ''}
					onConfirmClick={handleConfirmAction}
					onCancelClick={handleCancelAction}
					confirmationText={confirmationMeta?.confirmText || 'Confirm'}
					confirmButtonClass={confirmationMeta?.confirmButtonClass}
					cancelText="Cancel"
					class="modalBody"
					bothButton={true}
					footerClass="modalFooter"
				/>
			</DialogueBox>
		</div>
	);
};

export default UsersList;